/** @format */

import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
	FulfillmentButtonInterface,
	selectShipMode,
	selectShipToMeButtonText,
	setFulfillmentButtonsShipMode
} from '@pdp/add-to-cart/data-access';
import { combineLatest, Observable, Subject, takeUntil } from 'rxjs';
import {
	selectBundleShipToMeButtonText,
	selectEDDDisplayText,
	selectEDDDisplayVerbiage,
	selectKillswitch,
	selectResolvedSku
} from '@pdp/data-access/state';

import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { Store } from '@ngrx/store';
import { PdpFulfillmentButtonComponent } from '../../ui/fulfillment-button/fulfillment-button.component';
import {
	homefieldIconFulfillmentShippingFilled,
	homefieldIconFulfillmentShippingOutlined,
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService
} from '@dsg-tech/homefield-angular-icons';
import { AsyncPipe } from '@angular/common';

@Component({
	selector: 'pdp-feature-ship-to-me-button',
	templateUrl: './ship-to-me-button.component.html',
	styleUrl: './ship-to-me-button.component.scss',
	imports: [PdpFulfillmentButtonComponent, HomefieldIconLibraryModule, AsyncPipe]
})
export class ShipToMeButtonComponent implements OnInit, OnDestroy {
	private readonly cdr = inject(ChangeDetectorRef);
	private readonly productAnalyticsService = inject(ProductAnalyticsService);
	private readonly store = inject(Store);
	private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

	@Input() skuEDDEnabled: boolean;
	@Input() isBundle = false;

	shipToMeButtonText$: Observable<FulfillmentButtonInterface>;
	shipMode$ = this.store.select(selectShipMode);
	EDDDisplayText$ = this.store.select(selectEDDDisplayText);
	EDDDisplayVerbiage$ = this.store.select(selectEDDDisplayVerbiage);
	resolvedSku$ = this.store.select(selectResolvedSku);

	private UNSUBSCRIBE$: Subject<void> = new Subject();
	fulfillmentIconName = 'fulfillment-shipping-outlined';

	pdpSkuEDDEventEnabled = this.store.selectSignal(selectKillswitch('pdpSkuEDDEventEnabled'));

	constructor() {
		this.homefieldIconsLibraryService.registerIcons([
			homefieldIconFulfillmentShippingFilled,
			homefieldIconFulfillmentShippingOutlined
		]);
	}

	ngOnInit(): void {
		if (this.isBundle) {
			this.shipToMeButtonText$ = this.store.select(selectBundleShipToMeButtonText);
		} else {
			this.shipToMeButtonText$ = this.store.select(selectShipToMeButtonText);
		}

		combineLatest([this.EDDDisplayText$, this.resolvedSku$]).subscribe((data) => {
			if (this.pdpSkuEDDEventEnabled() && data[0]) {
				this.productAnalyticsService.triggerAnalyticsEvent('skuEDD', {
					skuEDD: data[0],
					id: data[1]?.partNumber
				});
			}
		});

		combineLatest([this.shipToMeButtonText$, this.shipMode$])
			.pipe(takeUntil(this.UNSUBSCRIBE$))
			.subscribe(([shipToMeButtonText, shipMode]) => {
				const buttonEnabled = !shipToMeButtonText.fulfillmentButtonDisabledClass;
				this.getFulfillmentIconName(shipMode, buttonEnabled);
				this.cdr.detectChanges();
			});
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	setShipMode() {
		this.store.dispatch(setFulfillmentButtonsShipMode({ shipMode: 'shipToMe' }));
	}

	getFulfillmentIconName(shipMode: string, buttonEnabled: boolean): void {
		if (shipMode === 'shipToMe' && buttonEnabled) {
			this.fulfillmentIconName = 'fulfillment-shipping-filled';
		} else {
			this.fulfillmentIconName = 'fulfillment-shipping-outlined';
		}
	}
}
